class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.kickisContext = this.getKickisContext();
    this.patternSearchEnabled = this.kickisContext.patternSearchEnabled !== false;
    this.patternSearchLimit = Math.max(1, Number(this.kickisContext.patternSearchLimit || 6));
    this.patternCatalogUrl = this.kickisContext.patternCatalogUrl || '';
    this.patternCatalogManifestUrl = this.kickisContext.patternCatalogManifestUrl || '';
    this.patternCatalogPromise = null;
    this.patterns = [];

    this.setupEventListeners();
  }

  getKickisContext() {
    const element = document.getElementById('KickisFavoritesContext');
    if (!element?.textContent?.trim()) return {};
    try {
      return JSON.parse(element.textContent);
    } catch (error) {
      console.warn('Kunde inte läsa Kickis-kontext för sök.', error);
      return {};
    }
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      this.renderPatternSearchResults(searchTerm);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
        this.renderPatternSearchResults(searchTerm);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.open();
  }

  async loadPatternCatalog() {
    if (!this.patternSearchEnabled || !this.patternCatalogUrl) return [];
    if (this.patternCatalogPromise) return this.patternCatalogPromise;

    this.patternCatalogPromise = (window.KickisPatternCatalog?.load
      ? window.KickisPatternCatalog.load(this.patternCatalogUrl, { manifestUrl: this.patternCatalogManifestUrl })
      : fetch(this.patternCatalogUrl, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.json();
        }))
      .then((catalog) => {
        const patterns = Array.isArray(catalog) ? catalog : (catalog.patterns || []);
        this.patterns = patterns.map((pattern) => this.normalisePattern(pattern));
        return this.patterns;
      })
      .catch((error) => {
        console.warn('Kunde inte läsa mönsterkatalogen för sök.', error);
        this.patternSearchEnabled = false;
        return [];
      });

    return this.patternCatalogPromise;
  }

  normalisePattern(pattern = {}) {
    const images = Array.isArray(pattern.imageUrls) && pattern.imageUrls.length
      ? pattern.imageUrls
      : [pattern.imageUrl].filter(Boolean);
    const variants = Array.isArray(pattern.variants) ? pattern.variants : [];
    const variantImages = variants.flatMap((variant) => Array.isArray(variant.imageUrls) ? variant.imageUrls : [variant.imageUrl].filter(Boolean));
    const yarnNames = Array.from(new Set([
      ...(Array.isArray(pattern.yarnNames) ? pattern.yarnNames : []),
      ...(Array.isArray(pattern.yarns) ? pattern.yarns.map((yarn) => yarn.title || yarn.storeTitle || yarn.sourceYarn) : []),
      ...variants.flatMap((variant) => Array.isArray(variant.yarns) ? variant.yarns.map((yarn) => yarn.title || yarn.storeTitle || yarn.sourceYarn) : []),
    ].filter(Boolean)));

    const searchText = [
      pattern.title,
      pattern.originalTitle,
      pattern.recipeId,
      pattern.type,
      pattern.category,
      pattern.target,
      pattern.difficulty,
      ...(Array.isArray(pattern.seasons) ? pattern.seasons : []),
      ...yarnNames,
      ...variants.map((variant) => variant.label),
    ].filter(Boolean).join(' ');

    return {
      ...pattern,
      imageUrl: images[0] || variantImages[0] || '',
      yarnNames,
      searchText: this.normaliseSearchText(searchText),
    };
  }

  async renderPatternSearchResults(searchTerm) {
    if (!this.patternSearchEnabled || !searchTerm?.trim()) return;

    const patterns = await this.loadPatternCatalog();
    if (searchTerm !== this.searchTerm || !patterns.length) return;

    this.querySelector('[data-kickis-pattern-search-results]')?.remove();

    const matches = this.getPatternMatches(searchTerm).slice(0, this.patternSearchLimit);
    if (!matches.length) return;

    const wrapper = document.createElement('div');
    wrapper.className = 'predictive-search__patterns-wrapper';
    wrapper.setAttribute('data-kickis-pattern-search-results', '');
    wrapper.innerHTML = `
      <h2 id="predictive-search-patterns" class="predictive-search__heading text-body caption-with-letter-spacing">Mönster</h2>
      <ul class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-patterns">
        ${matches.map((pattern, index) => this.renderPatternResult(pattern, index)).join('')}
      </ul>
    `;

    const secondGroup = this.querySelector('.predictive-search__result-group:last-child');
    const searchButton = this.querySelector('#predictive-search-option-search-keywords');
    if (secondGroup) {
      secondGroup.prepend(wrapper);
    } else if (searchButton?.parentElement) {
      searchButton.parentElement.insertBefore(wrapper, searchButton);
    } else {
      this.predictiveSearchResults.appendChild(wrapper);
    }
  }

  getPatternMatches(searchTerm) {
    const query = this.normaliseSearchText(searchTerm);
    if (!query) return [];

    return this.patterns
      .filter((pattern) => pattern.searchText.includes(query))
      .sort((a, b) => String(a.title || '').localeCompare(String(b.title || ''), 'sv'));
  }

  renderPatternResult(pattern, index) {
    const routeRoot = window.Shopify?.routes?.root || '/';
    const url = `${routeRoot}pages/monster/${encodeURIComponent(pattern.id)}`;
    const meta = [pattern.recipeId, pattern.type || pattern.category, (pattern.yarnNames || []).slice(0, 2).join(', ')]
      .filter(Boolean)
      .join(' · ');

    return `
      <li id="predictive-search-option-pattern-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false">
        <a href="${this.escapeHtml(url)}" class="predictive-search__item predictive-search__item--link-with-thumbnail predictive-search__item--pattern link link--text" tabindex="-1">
          ${pattern.imageUrl
            ? `<img class="predictive-search__image predictive-search__image--pattern" src="${this.escapeHtml(pattern.imageUrl)}" alt="${this.escapeHtml(pattern.title)}" width="64" height="64" loading="lazy">`
            : '<span class="predictive-search__image predictive-search__image--pattern predictive-search__image--placeholder" aria-hidden="true">M</span>'}
          <div class="predictive-search__item-content">
            <div class="predictive-search__item-vendor caption-with-letter-spacing">Mönster</div>
            <p class="predictive-search__item-heading h5">${this.escapeHtml(pattern.title || 'Mönster')}</p>
            ${meta ? `<p class="predictive-search__item-meta">${this.escapeHtml(meta)}</p>` : ''}
          </div>
        </a>
      </li>
    `;
  }

  normaliseSearchText(value) {
    return String(value || '')
      .trim()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ');
  }

  escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);

